/** Version 1 matches the Windows station's LabelTemplate/LabelElement wire format.
 * Coordinates are inches; font sizes are points. No executable markup or URLs.
 *
 * Compatibility rule: within schemaVersion 1, changes may only ADD optional fields,
 * tokens or rules. Never remove or rename a field, token or rule, and never tighten
 * a limit: layouts already saved (and cached on stations) must keep validating.
 * Anything else needs schemaVersion 2 plus a migration of the saved layouts. */
export const LABEL_KINDS = ["shelf", "weighed", "prepared", "receipt"] as const;
export type LabelKind = (typeof LABEL_KINDS)[number];
export const LABEL_TITLES: Record<LabelKind, string> = {
  shelf: "Shelf",
  weighed: "Weighed items",
  prepared: "Prepared food",
  receipt: "Receipt printer",
};
export const LABEL_TOKENS = [
  "ProductName",
  "Brand",
  "UnitPrice",
  "UnitPriceRaw",
  "TotalPrice",
  "WeightLb",
  "WeightLine",
  "WeightLineX",
  "SellByDate",
  "SellByLine",
  "SellByLineSpaced",
  "Hechsher",
  "Department",
  "Ingredients",
  "IngredientsLine",
  "Allergens",
  "AllergensLine",
  "Sku",
  "ProductId",
  "KosherSummary",
  "ShelfUnitPrice",
  "TaxBadge",
  "WicBadge",
  "Note",
  "LabelInfo",
  "Supervision",
  "StoreName",
  "Date",
  "DateShort",
  "SubLine",
  "PrimaryBarcode",
  "WeightedBarcode",
  "Barcode",
  // TASK-0223: the regular (catalog) price, printed only while the item is on sale.
  "RegularPrice",
  "RegularUnitPrice",
] as const;
export type LabelToken = (typeof LABEL_TOKENS)[number];
/** Operator names for label fields, shared by the console and the API's save errors. */
export const LABEL_FIELD_NAMES: Record<LabelToken, string> = {
  ProductName: "Product name",
  Brand: "Brand",
  UnitPrice: "Price",
  UnitPriceRaw: "Price (no dollar sign)",
  TotalPrice: "Package total",
  WeightLb: "Weight",
  WeightLine: "Weight and price per pound",
  WeightLineX: "Weight × price per pound",
  SellByDate: "Sell-by date",
  SellByLine: "Sell-by line",
  SellByLineSpaced: "Sell-by line (spaced)",
  Hechsher: "Kitchen hechsher (station)",
  Department: "Department",
  Ingredients: "Ingredients",
  IngredientsLine: "Ingredients with heading",
  Allergens: "Allergens",
  AllergensLine: "Allergens with heading",
  Sku: "Product number",
  ProductId: "Product number",
  KosherSummary: "Product kosher certification",
  ShelfUnitPrice: "Price with selling unit ($/Ea, $/lb)",
  TaxBadge: "Tax mark",
  WicBadge: "WIC mark",
  Note: "Note",
  LabelInfo: "Label information",
  Supervision: "Supervision",
  StoreName: "Store name",
  Date: "Print date and time",
  DateShort: "Print date",
  SubLine: "Department and kosher details",
  PrimaryBarcode: "Barcode number (text only)",
  WeightedBarcode: "Weighed barcode number (text only)",
  Barcode: "Barcode for this item",
  RegularPrice: "Regular price",
  RegularUnitPrice: "Regular price with selling unit ($/Ea, $/lb)",
};
/** The operator's name for one field on a label. */
export function labelElementName(e: { type: string; text?: string | undefined }): string {
  if (e.type === "Barcode") return "Barcode";
  if (e.type === "Image") return "Store logo";
  if (e.type !== "Text") return e.type === "Box" ? "Border" : "Line";
  const token = e.text?.match(/^\{(\w+)\}$/)?.[1];
  if (token) {
    const known = LABEL_TOKENS.find((t) => t === token);
    return known ? LABEL_FIELD_NAMES[known] : token;
  }
  return e.text || "Custom text";
}
export const LABEL_PREDICATES = [
  "Always",
  "HasBrand",
  "HasHechsher",
  "HasSellBy",
  "HasIngredients",
  "HasAllergens",
  "HasSupervision",
  "HasNote",
  "HasDistinctNote",
  "Taxable",
  "WicEligible",
  "ShelfJob",
  "WeighedJob",
  "NeedsWeight",
  "UseQrBarcode",
  "UseEan13Barcode",
  "HasBarcodeEan13",
  "HasBarcodeShort",
  "HasAnyBarcode",
  "ShowFooterSupervision",
  "PreparedFood",
  "RouteToReceiptPrinter",
] as const;
export type LabelElement = {
  type: "Text" | "Barcode" | "Box" | "Line" | "Image";
  x: number;
  y: number;
  width: number;
  height: number;
  text?: string | undefined;
  fontFamily?: string | undefined;
  fontSize?: number | undefined;
  bold?: boolean | undefined;
  italic?: boolean | undefined;
  align?: "Left" | "Center" | "Right" | undefined;
  wrap?: boolean | undefined;
  autoFit?: boolean | undefined;
  visibleWhen?: string | null | undefined;
  color?: string | undefined;
  imageSource?: "logo" | "" | undefined;
  barcodePayloadToken?: (typeof LABEL_TOKENS)[number] | undefined;
  barcodeFormat?: "Auto" | "Ean13" | "Qr" | undefined;
  barcodeHumanReadable?: boolean | undefined;
  strokeThickness?: number | undefined;
  strokeColor?: string | undefined;
  fillColor?: string | undefined;
};
export type LabelDesign = {
  widthInches: number;
  heightInches: number;
  template: { schemaVersion: 1; name: string; elements: LabelElement[] };
};
/** A saved store layout. `savedAt`/`savedBy` are read-only metadata the API adds
 * from the version history; stations ignore them. */
export type SavedLabelDesign = {
  revision: string;
  design: LabelDesign;
  savedAt?: string | null;
  savedBy?: string | null;
};
/** One entry in a label type's version history, newest first. */
export type LabelTemplateRevision = {
  revision: string;
  savedAt: string;
  savedBy: string | null;
  /** The older version this one was restored from, if it was a restore. */
  restoredFrom: string | null;
  /** This version is the one stations get now. */
  current: boolean;
  /** False when this version no longer matches the label format and can't be restored. */
  readable: boolean;
};
export type LabelTemplateHistory = {
  /** The live revision, even when its stored layout can't be read (so a save can replace it). */
  currentRevision: string | null;
  /** False when a layout is saved but no longer matches the label format. */
  currentReadable: boolean;
  revisions: LabelTemplateRevision[];
};
export type LabelDesigns = Record<LabelKind, SavedLabelDesign | null>;
export function defaultLabelDesign(kind: LabelKind): LabelDesign {
  const widthInches = kind === "shelf" ? 2.25 : kind === "receipt" ? 3.15 : 3;
  const heightInches = kind === "shelf" ? 1.25 : kind === "receipt" ? 4.5 : 2;
  const text = (value: string, y: number, height: number, fontSize = 10, visibleWhen?: string): LabelElement => ({
    type: "Text",
    x: 0.1,
    y,
    width: widthInches - 0.2,
    height,
    text: value,
    fontFamily: "Arial",
    fontSize,
    bold: false,
    wrap: true,
    autoFit: true,
    ...(visibleWhen ? { visibleWhen } : {}),
  });
  if (kind === "shelf")
    return {
      widthInches,
      heightInches,
      template: {
        schemaVersion: 1,
        name: LABEL_TITLES[kind],
        elements: [
          { ...text("{ProductName}", 0.08, 0.3, 11), bold: true },
          { ...text("{UnitPrice}", 0.46, 0.35, 20), x: 1.13, width: 1.02, bold: true, align: "Right" },
          { ...text("{ShelfUnitPrice}", 0.84, 0.16, 7), x: 1.13, width: 1.02, align: "Right" },
          {
            type: "Barcode",
            x: 0.1,
            y: 0.49,
            width: 0.62,
            height: 0.62,
            barcodePayloadToken: "PrimaryBarcode",
            barcodeFormat: "Qr",
          },
          { ...text("{WicBadge}", 1.03, 0.14, 8, "WicEligible"), x: 1.76, width: 0.39, bold: true },
        ],
      },
    };
  if (kind === "weighed" || kind === "prepared") {
    // 3 × 2 in, the label station's default roll for both (owner decision 2026-09-29).
    // Weighed items also prints prepared food sold by the pound, so it carries the
    // ingredient and allergen lines too; each shows only when the item has them.
    const weighed = kind === "weighed";
    const right = 1.55;
    const rightWidth = widthInches - right - 0.1;
    const leftWidth = right - 0.15;
    return {
      widthInches,
      heightInches,
      template: {
        schemaVersion: 1,
        name: LABEL_TITLES[kind],
        elements: [
          { ...text("{ProductName}", 0.06, 0.28, 12), bold: true },
          { ...text(weighed ? "{TotalPrice}" : "{UnitPrice}", 0.36, 0.34, 20), width: leftWidth, bold: true },
          {
            ...text(weighed ? "{WeightLine}" : "{Brand}", 0.36, 0.34, 8, weighed ? undefined : "HasBrand"),
            x: right,
            width: rightWidth,
          },
          { ...text("{SellByLine}", 0.72, 0.16, 8, "HasSellBy"), width: leftWidth },
          { ...text("{Hechsher}", 0.72, 0.16, 8, "HasHechsher"), x: right, width: rightWidth },
          text("{IngredientsLine}", 0.9, 0.42, 6, "HasIngredients"),
          text("{AllergensLine}", 1.33, 0.15, 6, "HasAllergens"),
          {
            type: "Barcode",
            x: 0.1,
            y: 1.5,
            width: 2.3,
            height: 0.44,
            barcodePayloadToken: weighed ? "WeightedBarcode" : "PrimaryBarcode",
            barcodeFormat: "Auto",
          },
          { ...text("{WicBadge}", 1.76, 0.16, 8, "WicEligible"), x: 2.55, width: 0.35, bold: true },
        ],
      },
    };
  }
  // Receipt printer: weighed and fixed-price items share one tall label, with a
  // weighed / fixed-price pair for the price and the barcode.
  const elements: LabelElement[] = [
    { ...text("{ProductName}", 0.08, 0.32, 12), bold: true },
    { ...text("{TotalPrice}", 0.42, 0.32, 20, "NeedsWeight"), bold: true },
    text("{WeightLine}", 0.78, 0.18, 9, "NeedsWeight"),
    text("{SellByLine}", 0.99, 0.18, 9, "HasSellBy"),
    text("{Hechsher}", 1.19, 0.18, 9, "HasHechsher"),
    {
      type: "Barcode",
      x: 0.1,
      y: 1.43,
      width: widthInches - 0.65,
      height: 0.42,
      barcodePayloadToken: "WeightedBarcode",
      barcodeFormat: "Auto",
      visibleWhen: "NeedsWeight",
    },
    {
      ...text("{WicBadge}", heightInches - 0.24, 0.16, 8, "WicEligible"),
      x: widthInches - 0.45,
      width: 0.35,
      bold: true,
    },
    {
      type: "Barcode",
      x: 0.1,
      y: 1.43,
      width: widthInches - 0.65,
      height: 0.42,
      barcodePayloadToken: "PrimaryBarcode",
      barcodeFormat: "Auto",
      visibleWhen: "!NeedsWeight",
    },
    { ...text("{UnitPrice}", 0.42, 0.32, 20, "!NeedsWeight"), bold: true },
    text("{IngredientsLine}", 2.05, 0.85, 10, "HasIngredients"),
    text("{AllergensLine}", 3.0, 0.5, 10, "HasAllergens"),
    text("{Supervision}", 3.6, 0.45, 10, "HasSupervision"),
  ];
  return { widthInches, heightInches, template: { schemaVersion: 1, name: LABEL_TITLES[kind], elements } };
}
